import { Client, Scheduler as FayeScheduler } from 'faye';
import { batchActions } from 'redux-batched-actions';
import _ from 'underscore';

import {
    showSettings,
    addTypingIndicatorEvent,
    removeTypingIndicatorEvent,
    hideChannelPage,
    hideConnectNotification,
    setConnectionState,
    setActiveConversationId,
    showErrorNotification,
} from '../actions/app-state';
import { login, setAuth } from '../actions/auth';
import {
    addMessage,
    incrementUnreadCount,
    rejectMessage,
    removeMessage,
    replaceMessage,
    resetUnreadCount,
    fetchUserConversation,
    resetConversation,
    setConversation,
    addCompletedUpload,
    completeUpload,
    setAppMakerLastRead,
    getConversation,
    refreshUserConversations,
    addParticipant,
    removeParticipant,
    setParticipantLastRead,
} from '../actions/conversation';
import { cancelSMSLink, failChannelLink, failSMSLink, matchSMSLink, resetIntegrations } from '../actions/integrations';
import { setUser, updateUser as updateUserState } from '../actions/user';
import { WIDGET_STATE } from '../constants/app';
import { CONNECTION_STATE } from '../constants/connection-state';
import {
    CONVERSATION_EVENTS,
    LINK_EVENTS,
    PARTICIPANT_EVENTS,
    TYPING_EVENTS,
    UPLOAD_EVENTS,
} from '../constants/events';
import { ANIMATION_TIMINGS } from '../constants/styles';
import { store } from '../store';
import { getIntegrationCategory } from '../utils/app';
import { getClientId, getSessionId } from '../utils/client';
import { observable } from '../utils/events';
import { getUploadError } from '../utils/errors';
import * as logger from '../utils/logger';
import { isCurrentUserMessage } from '../utils/message';
import { setItem } from '../utils/storage';

let client;
let subscription;
let connectionInProgress;

const getScheduler = ({ retryInterval = 15, maxConnectionAttempts = 5 }) => {
    return class Scheduler extends FayeScheduler {
        constructor(...args) {
            super(...args);
            this.client = client;
        }

        getInterval() {
            return retryInterval;
        }

        succeed() {
            if (this.client === client) {
                store.dispatch(setConnectionState(CONNECTION_STATE.CONNECTED));
            }
        }

        fail() {
            if (this.client === client) {
                const { user } = store.getState();

                if (user._id) {
                    store.dispatch(setConnectionState(CONNECTION_STATE.DISCONNECTED_WITH_RETRY));
                }
            }
        }

        isDeliverable() {
            // Check to see if this delivery retry loop is for a faye client that has been discarded
            // When we call disconnectFaye we want any pending retry loops associated to that client to cease
            if (this.client !== client) {
                return false;
            }

            // Prevent a loss of internet connectivity from exhausting retry attempts
            // Note that navigator.onLine may not work in Opera or Android WebViews
            // https://developer.mozilla.org/en-US/docs/Web/API/NavigatorOnLine/onLine#Browser_compatibility
            if (navigator.onLine === false) {
                this.attempts = 0;
                return true;
            }

            const isDeliverable = super.isDeliverable();

            if (!isDeliverable) {
                store.dispatch(setConnectionState(CONNECTION_STATE.DISCONNECTED));
                return false;
            }

            const { channel } = this.message;

            // target only setup messages
            if (['/meta/handshake', '/meta/connect', '/meta/subscribe'].includes(channel)) {
                const shouldRetry = this.attempts < maxConnectionAttempts;

                if (!shouldRetry) {
                    store.dispatch(setConnectionState(CONNECTION_STATE.DISCONNECTED));
                }

                return shouldRetry;
            }

            return true;
        }
    };
};

export const handleFayeReconnection = _.debounce(() => {
    store.dispatch(refreshUserConversations({ keepConvoWithFetchedMessages: false }));
}, 200);

function getClient() {
    if (!client) {
        const {
            config: { realtime, appId },
            auth,
            user,
        } = store.getState();

        const Scheduler = getScheduler(realtime);

        client = new Client(realtime.baseUrl, {
            scheduler: Scheduler,
        });

        client.addExtension({
            outgoing: (message, callback) => {
                if (message.channel === '/meta/subscribe') {
                    message.ext = {
                        appUserId: user._id,
                        appId,
                    };

                    if (auth.jwt) {
                        message.ext.jwt = auth.jwt;
                    } else if (auth.sessionToken) {
                        message.ext.sessionToken = auth.sessionToken;
                    }
                }

                callback(message);
            },
        });

        client.on('transport:down', () => {
            logger.debug('transport:down');
        });

        client.on('transport:up', function () {
            const {
                user: { _id: userId },
            } = store.getState();

            logger.debug('transport:up');
            store.dispatch(setConnectionState(CONNECTION_STATE.CONNECTED));

            if (userId) {
                handleFayeReconnection();
            }
        });
    }

    return client;
}

function ignoreNonAvailableConversationEvents(events, getState) {
    const { conversations } = getState();
    return events.filter(({ conversation }) => conversations[conversation._id]);
}

function ignoreNonCurrentConversationEvents(events, getState) {
    const {
        appState: { activeConversationId },
    } = getState();
    return events.filter(({ conversation }) => conversation._id === activeConversationId);
}

function ignoreNonCurrentConversationLinkEvents(events, getState) {
    const {
        appState: { activeConversationId },
        conversations,
    } = getState();

    if (activeConversationId === 'default') {
        return [];
    }

    return events.filter(({ conversation, isMerge, type }) => {
        if (conversation._id === activeConversationId) {
            return true;
        }

        if (type !== 'link') {
            return conversations[conversation._id];
        }

        return !!isMerge;
    });
}

function getNonCurrentConversationMessageEvents(events, currentConversationId, currentUserId) {
    return events.filter(({ conversation, type, message }) => {
        if (type === 'message' && conversation._id !== currentConversationId) {
            return !isCurrentUserMessage(message, currentUserId);
        }
    });
}

function resolveMessageActions(event, conversationsState) {
    const {
        conversation: { _id: conversationId },
    } = event;
    const targetConversation = conversationsState[conversationId];
    if (!targetConversation) {
        return ['REFRESH_LIST', 'FETCH_CONVERSATION'];
    }

    if (targetConversation.messagesUpdated) {
        return ['APPEND_MESSAGE'];
    }

    return ['FETCH_CONVERSATION'];
}

function addMessageToConversationHandler(event, dispatch, getState) {
    const {
        config: { integrationId },
        conversations,
        ui: { isAtBottom },
        appState: { widgetState, settingsVisible, activeConversationId },
        user: { _id: userId },
    } = getState();
    const {
        message,
        conversation: { _id: conversationId },
    } = event;
    const conversation = conversations[conversationId];
    const { messages } = conversation;
    if (message.source.id === getClientId(integrationId) && message.source.sessionId === getSessionId()) {
        if (!['file', 'image'].includes(message.type)) {
            return;
        }

        const existingPendingMessage = messages.find((m) => m._id === message._id);

        if (existingPendingMessage) {
            dispatch(
                replaceMessage(
                    conversationId,
                    {
                        _id: message._id,
                    },
                    message,
                ),
            );

            return dispatch(completeUpload(existingPendingMessage, message, conversationId));
        }

        return dispatch(addCompletedUpload(conversationId, message));
    }

    dispatch(addMessage(conversationId, message));

    if (conversationId === activeConversationId && !isCurrentUserMessage(message, userId)) {
        dispatch(removeTypingIndicatorEvent(message.role, message.authorId));
    }

    if (message.role === 'appUser' && message.authorId === userId) {
        return dispatch(resetUnreadCount(conversationId));
    }

    dispatch(incrementUnreadCount(conversationId));

    const widgetIsOpen = widgetState === WIDGET_STATE.OPENED || widgetState === WIDGET_STATE.EMBEDDED;
    if (widgetIsOpen && !settingsVisible && isAtBottom && message.authorId === userId) {
        // Mark the message as read if the user can see it
        return dispatch(resetUnreadCount(conversationId));
    }
}

function handleMessageEvents(events) {
    return (dispatch, getState) => {
        const {
            conversations,
            appState: { activeConversationId },
            user,
        } = getState();
        events.forEach((event) => {
            const actionTypes = resolveMessageActions(event, conversations);
            actionTypes.forEach((actionType) => {
                switch (actionType) {
                    case 'REFRESH_LIST':
                        return dispatch(refreshUserConversations());
                    case 'FETCH_CONVERSATION':
                        return dispatch(getConversation(event.conversation._id));
                    case 'APPEND_MESSAGE':
                        return addMessageToConversationHandler(event, dispatch, getState);
                }
            });
        });

        getNonCurrentConversationMessageEvents(events, activeConversationId, user._id).forEach(
            ({ message, conversation }) => {
                observable.trigger('message:received', message, { conversation });
            },
        );
    };
}

function handleStartTypingEvent(event, dispatch) {
    const {
        activity: { role, data, appUserId },
    } = event;
    return dispatch(addTypingIndicatorEvent(data, role, appUserId));
}

function handleStopTypingEvent(event, dispatch) {
    const {
        activity: { role, appUserId },
    } = event;

    return dispatch(removeTypingIndicatorEvent(role, appUserId));
}

function handleConversationReadEvent(event, dispatch, getState) {
    const {
        appState: { activeConversationId },
        user: currentUser,
    } = getState();
    const {
        activity: { role, data, appUserId, type },
        conversation,
    } = event;
    const conversationPayload = { conversation: { _id: conversation._id } };
    const dataPayload = { role };

    if (role === 'appMaker') {
        observable.trigger(type, { ...dataPayload, lastRead: conversation.appMakerLastRead }, conversationPayload);
        return dispatch(setAppMakerLastRead(activeConversationId, conversation.appMakerLastRead));
    }

    if (role === 'appUser' && appUserId !== currentUser._id) {
        observable.trigger(type, { ...dataPayload, lastRead: data.lastRead, appUserId }, conversationPayload);
        return dispatch(setParticipantLastRead(activeConversationId, { ...data, appUserId }));
    }
}

function handleActivityEvents(events) {
    return (dispatch, getState) => {
        ignoreNonCurrentConversationEvents(events, getState).forEach((event) => {
            const {
                activity: { type },
            } = event;

            switch (type) {
                case TYPING_EVENTS.START:
                    return handleStartTypingEvent(event, dispatch);
                case TYPING_EVENTS.STOP:
                    return handleStopTypingEvent(event, dispatch);
                case CONVERSATION_EVENTS.READ: {
                    return handleConversationReadEvent(event, dispatch, getState);
                }
            }
        });
    };
}

function updateUser(currentAppUser, nextAppUser, client, conversation) {
    return (dispatch, getState) => {
        if (currentAppUser._id !== nextAppUser._id) {
            const {
                config: { integrationId },
                auth: { jwt },
            } = getState();
            const actions = [resetConversation(), resetIntegrations()];
            actions.push(
                setUser({
                    _id: nextAppUser._id,
                }),
            );
            setItem(`${integrationId}.appUserId`, nextAppUser._id);

            if (nextAppUser.sessionToken) {
                actions.push(
                    setAuth({
                        sessionToken: nextAppUser.sessionToken,
                    }),
                );
                setItem(`${integrationId}.sessionToken`, nextAppUser.sessionToken);
            }

            disconnectFaye();
            dispatch(batchActions(actions));

            if (nextAppUser.userId) {
                return dispatch(login(nextAppUser.userId, jwt));
            } else {
                return dispatch(fetchUserConversation());
            }
        } else {
            dispatch(
                batchActions([
                    setConversation(conversation),
                    setActiveConversationId(conversation._id),
                    updateUserState({
                        clients: [...currentAppUser.clients, client],
                    }),
                ]),
            );

            return dispatch(getConversation(conversation._id));
        }
    };
}

function handleLinkEvents(events) {
    return (dispatch, getState) => {
        ignoreNonCurrentConversationLinkEvents(events, getState).forEach(
            ({ type, appUser, client, err, conversation }) => {
                const {
                    user: currentAppUser,
                    appState: { visibleChannelCategory },
                } = getState();
                if (type === 'link') {
                    dispatch(hideConnectNotification());
                    if (getIntegrationCategory(client.platform) === visibleChannelCategory) {
                        dispatch(showSettings());
                        // add a delay to let the settings page animation finish
                        // if it wasn't open already
                        return setTimeout(() => {
                            dispatch(hideChannelPage());

                            // add a delay to let the channel page hide, then update the user
                            // why? React will just remove the channel page from the DOM if
                            // we update the user right away.
                            setTimeout(() => {
                                dispatch(updateUser(currentAppUser, appUser, client, conversation));
                            }, ANIMATION_TIMINGS.PAGE_TRANSITION);
                        }, ANIMATION_TIMINGS.PAGE_TRANSITION);
                    }

                    return dispatch(updateUser(currentAppUser, appUser, client, conversation));
                } else if (type === LINK_EVENTS.CANCELLED) {
                    if (client.platform === 'twilio' || client.platform === 'messagebird') {
                        return dispatch(cancelSMSLink(client));
                    } else {
                        return dispatch(hideChannelPage());
                    }
                } else if (type === LINK_EVENTS.MATCHED) {
                    if (client.platform === 'twilio' || client.platform === 'messagebird') {
                        return dispatch(matchSMSLink(client));
                    }
                } else if (type === LINK_EVENTS.FAILED) {
                    if (client && (client.platform === 'twilio' || client.platform === 'messagebird')) {
                        return dispatch(failSMSLink(err, client));
                    } else if (client && client.platform) {
                        return dispatch(failChannelLink(client.platform));
                    }
                }
            },
        );
    };
}

function handleUploadEvents(events) {
    return (dispatch, getState) => {
        ignoreNonAvailableConversationEvents(events, getState).forEach(
            ({ type, data, err, conversation: { _id: conversationId } }) => {
                if (type !== UPLOAD_EVENTS.FAILED) {
                    return;
                }

                const {
                    ui: { text },
                    conversations,
                } = getState();
                const { messages } = conversations[conversationId];
                const { shouldRemoveMessage, bannerText } = getUploadError(err, text);

                if (shouldRemoveMessage) {
                    const existingPendingMessage = messages.find((m) => m._id === data.messageId);

                    if (existingPendingMessage) {
                        dispatch(
                            removeMessage(conversationId, {
                                _id: data.messageId,
                            }),
                        );
                        dispatch(hideConnectNotification());
                        return dispatch(showErrorNotification(bannerText));
                    }
                }

                return dispatch(
                    rejectMessage(
                        conversationId,
                        {
                            _id: data.messageId,
                        },
                        err,
                    ),
                );
            },
        );
    };
}

export function handleParticipantEvents(events) {
    return (dispatch, getState) => {
        const {
            conversations,
            appState: { activeConversationId },
        } = getState();
        events
            .filter((event) => [PARTICIPANT_EVENTS.ADDED, PARTICIPANT_EVENTS.REMOVED].includes(event.type))
            .forEach(({ type, conversation, participant }) => {
                const { _id: conversationId } = conversation;

                observable.trigger(type, participant, { conversation });

                if (type === PARTICIPANT_EVENTS.REMOVED && !conversations[conversationId]) {
                    return;
                }

                if (type === PARTICIPANT_EVENTS.ADDED && activeConversationId === 'default') {
                    // empty storage => fetch user to set proper conversation
                    return dispatch(fetchUserConversation());
                }

                if (conversationId === activeConversationId) {
                    const participantAction = type === PARTICIPANT_EVENTS.ADDED ? addParticipant : removeParticipant;
                    return dispatch(participantAction(conversationId, participant));
                }

                return dispatch(refreshUserConversations());
            });
    };
}

function handleConversationEvents(events) {
    return (dispatch, getState) => {
        const {
            conversations,
            appState: { activeConversationId },
        } = getState();
        const handlers = events
            .filter((event) => [CONVERSATION_EVENTS.ADDED, CONVERSATION_EVENTS.REMOVED].includes(event.type))
            .map(({ type, conversation, participants }) => {
                const { _id: conversationId } = conversation;

                // for conversation:removed, participants won't exist
                observable.trigger(type, ..._.compact([participants, { conversation }]));

                if (type === CONVERSATION_EVENTS.REMOVED && !conversations[conversationId]) {
                    // conversation is not in current list.
                    return;
                }

                if (type === CONVERSATION_EVENTS.ADDED && activeConversationId === 'default') {
                    // empty storage => fetch user to set proper conversation
                    return dispatch(fetchUserConversation());
                }

                return dispatch(refreshUserConversations()).then(() => {
                    if (conversationId === activeConversationId && type === CONVERSATION_EVENTS.REMOVED) {
                        return dispatch(setConversation({ _id: conversationId, messagesUpdated: false }));
                    }
                });
            });
        return Promise.all(handlers);
    };
}

export function subscribeFaye(reattemptCount = 0) {
    const { dispatch, getState } = store;
    const {
        config: { appId, realtime },
        user: { _id },
    } = getState();

    if (!_id) {
        return Promise.resolve();
    }

    if (subscription) {
        return subscription;
    }

    const client = getClient();

    connectionInProgress = true;
    const pendingSubscription = client.subscribe(`/sdk/apps/${appId}/appusers/${_id}`, function ({ events }) {
        const messageEvents = events.filter(({ type }) => type === 'message');
        const activityEvents = events.filter(({ type }) => type === 'activity');
        const linkEvents = events.filter(({ type }) => type.startsWith('link'));
        const uploadEvents = events.filter(({ type }) => type.startsWith('upload'));
        const participantEvents = events.filter(({ type }) => type.startsWith('participant'));
        const conversationEvents = events.filter(({ type }) => type.startsWith('conversation'));

        if (messageEvents.length > 0) {
            dispatch(handleMessageEvents(messageEvents));
        }

        if (activityEvents.length > 0) {
            dispatch(handleActivityEvents(activityEvents));
        }

        if (linkEvents.length > 0) {
            dispatch(handleLinkEvents(linkEvents));
        }

        if (uploadEvents.length > 0) {
            dispatch(handleUploadEvents(uploadEvents));
        }

        if (participantEvents.length > 0) {
            dispatch(handleParticipantEvents(participantEvents));
        }

        if (conversationEvents.length > 0) {
            dispatch(handleConversationEvents(conversationEvents));
        }
    });

    return pendingSubscription.then(
        () => {
            if (!connectionInProgress) {
                // disconnectFaye was called before we received a response from the server for our
                // original connection attempt. Abort.
                return;
            }

            dispatch(setConnectionState(CONNECTION_STATE.CONNECTED));
            subscription = pendingSubscription;
        },
        (err) => {
            const { retryInterval = 15, maxConnectionAttempts = 5 } = realtime;
            const canReattempt =
                reattemptCount < maxConnectionAttempts - 1 && err.code === 401 && err.message === 'Unknown client';

            if (canReattempt) {
                dispatch(setConnectionState(CONNECTION_STATE.DISCONNECTED_WITH_RETRY));
            } else {
                dispatch(setConnectionState(CONNECTION_STATE.DISCONNECTED));
            }

            if (canReattempt) {
                disconnectFaye();

                // First reattempt immediately, otherwise wait retryInterval
                const delay = reattemptCount === 0 ? 0 : retryInterval * 1000;
                return new Promise((resolve) => {
                    setTimeout(() => {
                        return resolve(subscribeFaye(++reattemptCount));
                    }, delay);
                });
            } else {
                // Faye produces non-Error objects
                throw {
                    ...new Error(err.message),
                    ...err,
                    reattemptCount,
                };
            }
        },
    );
}

export function disconnectFaye() {
    connectionInProgress = false;

    if (subscription) {
        subscription.cancel();
        subscription = undefined;
    }

    if (client) {
        client.disconnect();
        client = undefined;
    }
}

export function reconnectFaye() {
    disconnectFaye();
    return subscribeFaye();
}
